import type { CustomerBranding } from '@/api/branding'
import { getContrastRatio, hexToRgb } from '@/utils/colorUtil'

// Mirrors the backend's host resolution in domain-model `customer.usecases.ts`, so the
// console only asks for a reseller's branding on a host the API can resolve to one.
const GRID_CONSOLE_HOST_SUFFIXES = ['.staging.app.gridverify.com', '.sandbox.app.gridverify.com', '.app.gridverify.com']
const RESERVED_GRID_SUBDOMAINS = ['staging', 'sandbox']
const GRID_OWNED_HOST_SUFFIXES = ['gridverify.com', '.herokuapp.com', 'localhost']

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/
const MAX_BORDER_RADIUS_PX = 200

/**
 * Whether a host is a reseller's console: a `<label>.app.gridverify.com` subdomain or the
 * reseller's own domain. Grid's own hosts answer with Grid's internal branding, which the
 * built-in theme already is, so they are not looked up.
 * @param rawHost - `window.location.hostname`
 * @returns true when the host can carry a reseller's branding
 */
export const isResellerHost = (rawHost: string): boolean => {
  const host = rawHost.trim().toLowerCase().replace(/^www\./, '')
  if (!host || /^[\d.:]+$/.test(host)) return false

  const consoleSuffix = GRID_CONSOLE_HOST_SUFFIXES.find(suffix => host.endsWith(suffix))
  if (consoleSuffix) {
    const label = host.slice(0, -consoleSuffix.length)
    return !!label && !label.includes('.') && !RESERVED_GRID_SUBDOMAINS.includes(label)
  }

  return !GRID_OWNED_HOST_SUFFIXES.some(suffix => host.endsWith(suffix))
}

/**
 * Whether a value is a `#RRGGBB` color, the only form the branding API accepts.
 * @param value - candidate color
 * @returns true for a six-digit hex color
 */
export const isBrandingHexColor = (value: unknown): value is string => typeof value === 'string' && HEX_COLOR.test(value)

/**
 * A font family name safe to write into a stylesheet. The name comes from the server and is
 * shown to every visitor of the reseller's host, so anything that could close the
 * declaration is dropped.
 * @param font - font family name
 * @returns the cleaned name, or an empty string
 */
export const sanitizeFontFamily = (font?: string | null): string => (font ?? '').replace(/[^A-Za-z0-9 -]/g, '').trim()

export type BrandingColorMode = 'light' | 'dark'

/** WCAG 2.1 AA for text, which also clears the 3:1 a filled button needs against the page. */
export const MIN_PRIMARY_CONTRAST = 4.5

// Grid's warm off-white and off-black backgrounds are part of Grid's look, so a branded
// console sits on neutral greys instead. Fixing these colours is what lets one contrast
// check stand for every place the primary colour is drawn.
export const BRANDED_MODE_COLORS: Record<BrandingColorMode, { page: string, surface: string, onPrimary: string, defaultPrimary: string }> = {
  light: {
    page: '#F7F7F7', surface: '#EFEFEF', onPrimary: '#FFFFFF', defaultPrimary: '#191917',
  },
  dark: {
    page: '#191919', surface: '#252525', onPrimary: '#262523', defaultPrimary: '#FFFFFF',
  },
}

/**
 * How well a primary colour reads in a mode: its lowest contrast against that mode's page,
 * surfaces, and the text and icons drawn on it.
 * @param hex - `#RRGGBB` colour
 * @param mode - the theme it would be used in
 * @returns the unrounded lowest ratio, and the colour it was measured against
 */
export const getPrimaryContrast = (hex: string, mode: BrandingColorMode) => {
  const { page, surface, onPrimary } = BRANDED_MODE_COLORS[mode]
  // `getContrastRatio` linearises below WCAG 2.0's 0.03928 rather than 2.1's 0.04045. No
  // 8-bit channel falls between the two, so for a hex colour the ratios are identical.
  return [page, surface, onPrimary]
    .map(against => ({ against, ratio: getContrastRatio(hex, against) }))
    .reduce((lowest, next) => (next.ratio < lowest.ratio ? next : lowest))
}

/**
 * Whether a colour can be a mode's primary: a `#RRGGBB` colour with at least
 * {@link MIN_PRIMARY_CONTRAST} against everything it meets there. The ratio is compared
 * unrounded, so 4.497:1 fails.
 * @param value - candidate colour
 * @param mode - the theme it would be used in
 * @returns true when the colour passes in that mode
 */
export const isContrastReadyPrimary = (value: unknown, mode: BrandingColorMode): value is string => (
  isBrandingHexColor(value) && getPrimaryContrast(value, mode).ratio >= MIN_PRIMARY_CONTRAST
)

/**
 * The primary colour a branded console uses in a mode. A colour saved before the contrast
 * check, or pulled from a website, can fail it, and the mode then keeps its default.
 * @param branding - the active branding
 * @param mode - light or dark theme
 * @returns the customer's colour when it passes in that mode, otherwise the mode's default
 */
export const getBrandedPrimary = (branding: CustomerBranding | null | undefined, mode: BrandingColorMode): string => {
  const color = mode === 'dark' ? branding?.primaryColorDark : branding?.primaryColor
  return isContrastReadyPrimary(color, mode) ? color : BRANDED_MODE_COLORS[mode].defaultPrimary
}

const primaryTokens = (hex: string) => {
  const { r, g, b } = hexToRgb(hex) as { r: number, g: number, b: number }
  const alpha = (value: number) => `rgba(${r}, ${g}, ${b}, ${value})`
  return [
    `--primary: ${hex};`,
    `--primary-light: ${alpha(0.1)};`,
    `--primary-highlight: ${alpha(0.2)};`,
    `--primary-faded: ${alpha(0.5)};`,
    `--primary-hover: ${alpha(0.8)};`,
    `--brand-mark: ${hex};`,
  ].join(' ')
}

// The switch's checked knob is an SVG image, which can't read a CSS variable, so it is
// redrawn with the brand colour. The selector out-ranks the knob utilities in `Switch`.
const switchKnobRule = (theme: string, hex: string) => `${theme} .switch-container .form-check-input:checked { background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23${hex.slice(1)}'/%3e%3c/svg%3e"); }`

// The checked checkbox (`content-checkbox-on` in tailwind.config.js) is an SVG image too; this is its path.
const CHECKBOX_ON_PATH = 'M17.673 9.73961C18.0815 9.3679 18.1113 8.73543 17.7396 8.32696C17.3679 7.91849 16.7354 7.88868 16.327 8.26039L17.673 9.73961ZM9.30769 16L8.57289 16.6783C8.94561 17.0821 9.57431 17.1094 9.98073 16.7396L9.30769 16ZM7.7348 12.8217C7.3602 12.4159 6.72754 12.3906 6.32172 12.7652C5.9159 13.1398 5.89059 13.7725 6.2652 14.1783L7.7348 12.8217ZM16.327 8.26039L8.63465 15.2604L9.98073 16.7396L17.673 9.73961L16.327 8.26039ZM10.0425 15.3217L7.7348 12.8217L6.2652 14.1783L8.57289 16.6783L10.0425 15.3217ZM21 12C21 16.9706 16.9706 21 12 21V23C18.0751 23 23 18.0751 23 12H21ZM12 21C7.02944 21 3 16.9706 3 12H1C1 18.0751 5.92487 23 12 23V21ZM3 12C3 7.02944 7.02944 3 12 3V1C5.92487 1 1 5.92487 1 12H3ZM12 3C16.9706 3 21 7.02944 21 12H23C23 5.92487 18.0751 1 12 1V3Z'

// The Tailwind class `checked:before:content-checkbox-on`, with its colons escaped as the generated CSS has them.
const CHECKBOX_ON_SELECTOR = String.raw`.checked\:before\:content-checkbox-on:checked::before`

const checkboxOnRule = (theme: string, hex: string) => `${theme} ${CHECKBOX_ON_SELECTOR} { content: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='${CHECKBOX_ON_PATH}' fill='%23${hex.slice(1)}'/%3E%3C/svg%3E"); }`

const THEME_SELECTORS: Record<BrandingColorMode, string> = { light: 'html:not([data-theme=dark])', dark: 'html[data-theme=dark]' }

/**
 * The stylesheet that re-skins the app with a customer's branding. It overrides the design
 * tokens from `tailwind/theme-tokens.js`: the page takes a neutral background, anything
 * drawn with `primary-*` takes the brand colour, or the mode's default when it has none
 * that passes the contrast check, and buttons and form controls take its radius. Other
 * fields that are not set keep Grid's defaults.
 * @param branding - the branding to apply
 * @returns CSS text
 */
export const buildBrandingCss = (branding: CustomerBranding): string => {
  const rules: string[] = []

  const radius = Number(branding.borderRadiusPx)
  if (branding.borderRadiusPx != null && Number.isFinite(radius)) {
    rules.push(`html:root { --border-radius: ${Math.min(Math.max(radius, 0), MAX_BORDER_RADIUS_PX)}px; }`)
  }
  // `html` raises each selector one step above the token's own, so the order the two
  // stylesheets load in does not matter. Each mode has its own colour: no single colour
  // can pass the contrast check on both a light page and a dark one.
  (['light', 'dark'] as const).forEach(mode => {
    const { page, onPrimary } = BRANDED_MODE_COLORS[mode]
    const primary = getBrandedPrimary(branding, mode)
    const lightPrimary = getBrandedPrimary(branding, 'light')
    rules.push(
      `${THEME_SELECTORS[mode]} { --gray-7: ${page}; --on-primary: ${onPrimary}; ${primaryTokens(primary)} --brand-mark-light: ${lightPrimary}; }`,
      switchKnobRule(THEME_SELECTORS[mode], primary),
      checkboxOnRule(THEME_SELECTORS[mode], primary),
    )
  })

  // Text is set in many families through arbitrary `font-[...]` utilities rather than one
  // token, so the brand font is forced over all of them. Monospaced text keeps its family,
  // and so does anything given one inline, such as a font picker's previews.
  const font = sanitizeFontFamily(branding.font)
  if (font) {
    const exempt = 'code, pre, kbd, samp, [class*="Mono"], [class*="mono"], [style*="font-family"], [style*="font-family"] *'
    rules.push(`body *:not(${exempt}) { font-family: "${font}", sans-serif !important; }`)
  }

  return rules.join('\n')
}

/**
 * A copy of a Lottie animation with its fills and strokes in another colour. The loading
 * animations are drawn in Grid's primary inside their JSON, where the theme tokens can't
 * reach.
 * @param animation - Lottie JSON
 * @param hex - `#RRGGBB` colour
 * @returns the recoloured animation
 */
export const recolorLottie = <T extends object>(animation: T, hex: string): T => {
  const { r, g, b } = hexToRgb(hex) as { r: number, g: number, b: number }
  const color = [r / 255, g / 255, b / 255, 1]
  // `a: 0` marks a colour that isn't animated, the only kind these files use.
  return JSON.parse(JSON.stringify(animation), (_key, value) => (
    (value?.ty === 'st' || value?.ty === 'fl') && value.c?.a === 0 ? { ...value, c: { ...value.c, k: color } } : value
  ))
}

// The saved row also carries ids and timestamps, which brand nothing; the name titles tabs but skins nothing.
const THEME_FIELDS: (keyof CustomerBranding)[] = ['logo', 'logoDark', 'favicon', 'primaryColor', 'primaryColorDark', 'font', 'borderRadiusPx']

/**
 * Whether a branding has anything to apply.
 * @param branding - branding to inspect
 * @returns true when at least one themed field is set
 */
export const hasBranding = (branding?: CustomerBranding | null): branding is CustomerBranding => !!branding
  && THEME_FIELDS.some(field => branding[field] !== null && branding[field] !== undefined && branding[field] !== '')
